import React from 'react';

import { useBreakpoint } from '$hooks/events';
import { ArrowRightIcon } from '$partials/icon';
import { Image } from '$partials/media';
import { Button, Tag } from '$partials/ui';
import { Carousel, CarouselPagination, CarouselSlide, CarouselWrapper } from '$partials/base/carousel';

import { formatPrice, formatRentFrequency, formatTagBanner } from '$utilities/format';
import { OffMarketBannerBlock } from '$partials/modules/blocks';
import { PartnershipCtaBox } from '$partials/listings/items';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/partials/modules.d.ts').FeaturedPropertiesImageCarousel}
 */
const FeaturedPropertiesImageCarousel = ({
    __typename,
    publicSellingStatus,
    images,
    height,
    statusTag
}) => {
    const breakpoint = useBreakpoint([ 'main', 'sm', 'md' ], 'main');

    return (
        <Carousel swiperConfig={{
            slidesPerView: 1,
            spaceBetween: 0,
            breakpoints: {
                640: {
                    slidesPerView: 1,
                    spaceBetween: 0,
                },
                1024: {
                    slidesPerView: 1,
                    spaceBetween: 1
                }
            },
        }}>
            <div className='overflow-hidden featured-properties-image'>
                <CarouselWrapper
                    className='swiper-wrapper'>
                    {__typename === 'RentalPropertyPage' && (
                        <div className='absolute z1'>
                            <Tag title={formatTagBanner(publicSellingStatus, statusTag)}/>
                        </div>
                    )}
                    {__typename === 'PropertyPage' && (
                        <div className='absolute z1'>
                            <Tag
                                bgColor={publicSellingStatus === 'Sold' ? 'white' : 'yellow'}
                                title={formatTagBanner(publicSellingStatus, statusTag)}/>
                        </div>
                    )}
                    {images?.map((item, i) => {
                        return (
                            <CarouselSlide
                                className='flex carousel-slide'
                                index={i}
                                key={`featured-properties-image-${item?.image?.id}`}>
                                <div
                                    style={{fontSize: height}}>
                                    <div
                                        className='flex width-100'
                                        style={{
                                            height: breakpoint === 'md' ? height : 'auto',
                                            width:  breakpoint === 'md' ? `${(item.image.width / item.image.height)}em` : '280px',
                                            maxWidth: '100%'
                                        }}>
                                        <Image {...item.image}/>
                                    </div>
                                </div>
                            </CarouselSlide>
                        );
                    })}
                </CarouselWrapper>
                {images?.length > 1 && (
                    <div
                        className='absolute z3 featured-properties-pagination transition-opacity'
                        style={{marginTop: '-25px'}}>
                        <CarouselPagination className='ml5 flex'/>
                    </div>
                )}
            </div>
        </Carousel>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').FeaturedProperties}
 */

const FeaturedProperties = ({
    title,
    items,
    buyListing,
    rentListing,
    offMarketBanner,
    partnershipCta
}) => {
    const properties = items.filter(item => item.page);
    const firstBatchItems = properties.slice(0, 2);
    const secondBatchItems = properties.slice(2, 4);
    const thirdBatchItems = properties.slice(4, 6);
    const remainingItems = partnershipCta[0] ? properties.slice(5) : properties.slice(6);

    const commonClass = 'text-decoration-reset px5 md-px6 pb5 pt5 bg-white grey border-top border-bottom flex md-justify-between flex-col';

    return (
        <div>
            <div className='col-12 flex flex-wrap border-bottom grey'>
                <div className='col-12 sm-col-7 pr7 py6 md-pr0 bg-white flex items-center pl5 sm-pl6'>
                    <h2 className='type-style-2 black'>{title}</h2>
                </div>
                <div className='flex-grow flex z1'>
                    {buyListing &&
                        <Button
                            as='a'
                            className='bg-black white type-style-5 items-center mr1'
                            href={buyListing.url}
                            look='fill'
                            renderIcon={<ArrowRightIcon />}>
                            Buy
                        </Button>
                    }
                    {rentListing &&
                        <Button
                            as='a'
                            className='bg-black white type-style-5 items-center ml1'
                            href={rentListing.url}
                            look='fill'
                            renderIcon={<ArrowRightIcon />}>
                            Rent
                        </Button>
                    }
                </div>
            </div>
            <div className='col-12 flex flex-wrap'>
                {firstBatchItems.length > 0 && firstBatchItems.map((item, idx) => {
                    const page = item.page;
                    const colSize = idx === 0 || idx === 3 || idx === 4 || idx === 7 ? 'small' : 'large';
                    const price = page.__typename === 'PropertyPage' ? formatPrice(page.priceGbp) : `${formatPrice(page.rentGbp)} ${formatRentFrequency(page.rentFrequency)}`;

                    if (colSize === 'small') {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8' } col-12 sm-col-6 md-col-5`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-10 md-col-8 pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`}>
                                    <div>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                    <div className='mt5'>
                                        <p className='type-style-5'>{page?.propertyName}</p>
                                        <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                    </div>
                                </div>
                            </a>
                        );
                    } else {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8'} col-12 sm-col-6 md-col-7`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-12 flex flex-wrap pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`} >
                                    <div className='col-12 md-col-4'>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <div className='md-pl8 col-10 md-col-6'>
                                        <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{page?.propertyName}</p>
                                            <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        );}
                })}

                {offMarketBanner?.length > 0 && offMarketBanner[0]?.title && (
                    <div className='col-12'>
                        <div>
                            <OffMarketBannerBlock {...offMarketBanner[0]}/>
                        </div>
                    </div>
                )}

                {secondBatchItems.length > 0 && secondBatchItems.map((item, i) => {
                    const page = item.page;
                    const idx = i + 2;
                    const colSize = idx === 0 || idx === 3 || idx === 4 || idx === 7 ? 'small' : 'large';
                    const price = page.__typename === 'PropertyPage' ? formatPrice(page.priceGbp) : `${formatPrice(page.rentGbp)} ${formatRentFrequency(page.rentFrequency)}`;

                    if (colSize === 'small') {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8' } col-12 sm-col-6 md-col-5 z1`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-10 md-col-8 pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`}>
                                    <div>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                    <div className='mt5'>
                                        <p className='type-style-5'>{page?.propertyName}</p>
                                        <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                    </div>
                                </div>
                            </a>
                        );
                    } else {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8'} col-12 sm-col-6 md-col-7 z1`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-12 flex flex-wrap pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`} >
                                    <div className='col-12 md-col-4'>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <div className='md-pl8 col-10 md-col-6'>
                                        <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{page?.propertyName}</p>
                                            <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        );
                    }
                })}

                {partnershipCta[0] ? (
                    partnershipCta[0].position === 'left' ? (
                        <>
                            <a
                                className={`${commonClass} sm-border-right md-flex-col-reverse md-pt8 md-pb6 col-12 sm-col-6 md-col-5 z1`}
                                href={partnershipCta[0].url}>
                                <PartnershipCtaBox
                                    {...partnershipCta[0]}
                                    size='small' />
                            </a>
                            <a
                                className={`${commonClass} sm-border-left md-pt6 md-pb8 col-12 sm-col-6 md-col-7`}
                                href={thirdBatchItems[0]?.page?.url}>
                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={thirdBatchItems[0]?.page?.listingMedia}
                                    {...thirdBatchItems[0]?.page}/>

                                <div className='black col-12 flex flex-wrap pt5 md-pt8'>
                                    <div className='col-12 md-col-4'>
                                        <p className='type-style-5'>
                                            {thirdBatchItems[0]?.page?.__typename === 'PropertyPage' ? formatPrice(thirdBatchItems[0]?.page.priceGbp) : `${formatPrice(thirdBatchItems[0]?.page.rentGbp)} ${formatRentFrequency(thirdBatchItems[0]?.page.rentFrequency)}`}
                                        </p>
                                    </div>
                                    <div className='md-pl8 col-10 md-col-6'>
                                        <h3 className='type-style-3 mb4'>{thirdBatchItems[0]?.page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{thirdBatchItems[0]?.page?.propertyName}</p>
                                            <p className='type-style-5'>{thirdBatchItems[0]?.page?.area} {thirdBatchItems[0]?.page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </>
                    ) : (
                        <>
                            <a
                                className={`${commonClass} sm-border-right md-flex-col-reverse md-pt8 md-pb6 col-12 sm-col-6 md-col-5 z1`}
                                href={thirdBatchItems[0]?.page?.url}>
                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={thirdBatchItems[0]?.page?.listingMedia}
                                    {...thirdBatchItems[0]?.page}/>

                                <div className='black col-10 md-col-8 pt5 md-pt0 md-mb8'>
                                    <div>
                                        <p className='type-style-5'>
                                            {thirdBatchItems[0]?.page?.__typename === 'PropertyPage' ? formatPrice(thirdBatchItems[0]?.page.priceGbp) : `${formatPrice(thirdBatchItems[0]?.page.rentGbp)} ${formatRentFrequency(thirdBatchItems[0]?.page.rentFrequency)}`}
                                        </p>
                                    </div>
                                    <h3 className='type-style-3 mb4'>{thirdBatchItems[0]?.page?.heading}</h3>
                                    <div className='mt5'>
                                        <p className='type-style-5'>{thirdBatchItems[0]?.page?.propertyName}</p>
                                        <p className='type-style-5'>{thirdBatchItems[0]?.page?.area} {thirdBatchItems[0]?.page?.outwardCode}</p>
                                    </div>
                                </div>
                            </a>
                            <a
                                className={`${commonClass} sm-border-left md-pt6 md-pb8 col-12 sm-col-6 md-col-7`}
                                href={partnershipCta[0].url}>
                                <PartnershipCtaBox
                                    {...partnershipCta[0]}
                                    size='large' />
                            </a>
                        </>
                    )
                ) : (
                    thirdBatchItems.length > 0 && thirdBatchItems.map((item, idx) => {
                        const page = item.page;
                        const colSize = idx === 0 || idx === 3 || idx === 4 || idx === 7 ? 'small' : 'large';
                        const price = page.__typename === 'PropertyPage' ? formatPrice(page.priceGbp) : `${formatPrice(page.rentGbp)} ${formatRentFrequency(page.rentFrequency)}`;

                        if (colSize === 'small') {
                            return (
                                <a
                                    className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8' } col-12 sm-col-6 md-col-5`}
                                    href={page?.url}
                                    key={`featured-property-${page?.id}-${idx}`}>

                                    <FeaturedPropertiesImageCarousel
                                        height='360px'
                                        images={page.listingMedia}
                                        {...page}/>

                                    <div className={`black col-10 md-col-8 pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`}>
                                        <div>
                                            <p className='type-style-5'>
                                                {price}
                                            </p>
                                        </div>
                                        <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{page?.propertyName}</p>
                                            <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </a>
                            );
                        } else {
                            return (
                                <a
                                    className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8'} col-12 sm-col-6 md-col-7`}
                                    href={page?.url}
                                    key={`featured-property-${page?.id}-${idx}`}>

                                    <FeaturedPropertiesImageCarousel
                                        height='360px'
                                        images={page.listingMedia}
                                        {...page}/>

                                    <div className={`black col-12 flex flex-wrap pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`} >
                                        <div className='col-12 md-col-4'>
                                            <p className='type-style-5'>
                                                {price}
                                            </p>
                                        </div>
                                        <div className='md-pl8 col-10 md-col-6'>
                                            <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                            <div className='mt5'>
                                                <p className='type-style-5'>{page?.propertyName}</p>
                                                <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            );
                        }
                    })
                )}

                {remainingItems.length > 0 && remainingItems.map((item, i) => {
                    const page = item.page;
                    const idx = i + 2;
                    const colSize = idx === 0 || idx === 3 || idx === 4 || idx === 7 ? 'small' : 'large';
                    const commonClass = 'text-decoration-reset px5 md-px6 pb5 pt5 bg-white grey border-top border-bottom flex md-justify-between flex-col';
                    const price = page.__typename === 'PropertyPage' ? formatPrice(page.priceGbp) : `${formatPrice(page.rentGbp)} ${formatRentFrequency(page.rentFrequency)}`;

                    if (colSize === 'small') {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8' } col-12 sm-col-6 md-col-5 z1`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-10 md-col-8 pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`}>
                                    <div>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                    <div className='mt5'>
                                        <p className='type-style-5'>{page?.propertyName}</p>
                                        <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                    </div>
                                </div>
                            </a>
                        );
                    } else {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8'} col-12 sm-col-6 md-col-7 z1`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <FeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-12 flex flex-wrap pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`} >
                                    <div className='col-12 md-col-4'>
                                        <p className='type-style-5'>
                                            {price}
                                        </p>
                                    </div>
                                    <div className='md-pl8 col-10 md-col-6'>
                                        <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{page?.propertyName}</p>
                                            <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        );
                    }
                })}
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default FeaturedProperties;
